import {
  DeleteEntityPopUpSpecifics,
  DeleteErrorConfigs,
  ValidDeleteStates,
} from '../../../../networkPopUps/popUps/delete/types';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../../../utils/request';
import { DeleteActionState, GlobalNetworkAction, NetworkActionsToPopupsMap, NetworkV2Analytics } from '../../types';
import { BaseNetworkActionValidator } from '../../../factory/action-validators/base.network.action.validator';
import { NetworkActionValidatorsFactory } from '../../../factory/NetworkActionValidatorsFactory';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { TimeUnit } from '@nirvana/shared/constants';
import { BaseNetworkActionHelper } from '../base.network.action.helper';
import { translateFn } from '../../../../../utils/translator.helper';
import { htmlEncode } from '../../../../../utils/escape.utils';
import { NetworkPopUpFactory, NetworkPopUpType } from '../../../../networkPopUps/network.pop-up.factory';

interface ValidationResponse {
  isValid: boolean;
  errorCode?: string;
}

export class DeleteActionHelper extends BaseNetworkActionHelper {
  public static async processAction(selectedTargets: Array<ProtectedEntity>): Promise<DeleteEntityPopUpSpecifics> {
    const deleteErrorConfigs: DeleteErrorConfigs = {
      [DeleteActionState.NON_DELETABLE_AWS_ENTITIES]: {
        title: translateFn('Cannot delete entities'),
        message: translateFn(
          'You cannot delete EC2 integration entities such as groups, accounts, regions, availability zones, {entity_type} groups, or instances that are not in a terminated state. Change your selection and try again.',
          {
            entity_type: Translator.translateProtectedEntityNameByType(
              'Terminated Managed Instances',
              Constants.ProtectedEntities.EPS.TERMINATED_INSTANCES_GROUP,
              Constants.ProductTypes.GRAVITY_ZONE_CLOUD
            ),
          }
        ),
      },
      [DeleteActionState.NON_DELETABLE_VMWARE_ENTITIES]: {
        title: translateFn('Cannot delete entities'),
        message: translateFn(
          'You cannot delete endpoints that are listed under a VMware inventory node.',
          {
            entity_type: Translator.translateProtectedEntityNameByType(
              'Terminated Managed Instances',
              Constants.ProtectedEntities.EPS.VMWARE_VIRTUAL_MACHINE,
              Constants.ProtectedEntities.EPS.VMWARE_CENTER,
            ),
          }
        ),
      },
      [DeleteActionState.DEFAULT_TARGETS_IN_SELECTED_ITEMS]: {
        title: translateFn('Cannot delete folders'),
        message: translateFn('You cannot delete default GravityZone folders.'),
      },
      [DeleteActionState.CONTAINER_IN_SELECTED_ITEMS]: {
        title: translateFn('Cannot delete containers'),
        message: translateFn('You cannot delete the selected containers.'),
      },
      [DeleteActionState.COMPANY_IN_SELECTED_ITEMS]: {
        title: translateFn('Cannot delete companies'),
        message: translateFn(
          `You cannot delete the selected companies. To perform this action, go to the <b> Companies </b> section.`
        ),
      },
      [DeleteActionState.CONTAINER_HOST_FOLDER_IN_SELECTED_ITEMS]: {
        title: translateFn('Cannot delete container host folders'),
        message: translateFn(
          `You cannot delete folders labeled as container hosts within the <b> {folder_name} </b> directory.`,
          {
            folder_name: htmlEncode(
              Translator.translateProtectedEntityType(Constants.ProtectedEntities.CONTAINERS.TYPE_COLLECTION)
            ),
          }
        ),
      },
      [DeleteActionState.NOT_EMPTY_COMPANY_FOLDER_IN_SELECTED_ITEMS]: {
        title: translateFn('Cannot delete folders'),
        message: translateFn('You cannot delete populated company folders.'),
      },
      [DeleteActionState.CONTAINER_GROUP_HAS_CONTAINER_HOSTS_IN_SELECT_ITEMS]: {
        title: translateFn('Cannot delete folders'),
        message: translateFn('You cannot delete folders that include container hosts.'),
      },
      [DeleteActionState.CONTAINER_HOST_VM_PROTECTED_BY_SECURITY_CONTAINER_IN_SELECTED_ITEMS]: {
        title: translateFn('Cannot delete container hosts'),
        message: translateFn('You cannot delete container hosts managed by Security Containers.'),
      },
      [DeleteActionState.CONTAINER_HOST_VM_ONLINE_IN_SELECTED_ITEMS]: {
        title: translateFn('Cannot delete container hosts'),
        message: translateFn('You cannot delete container hosts that have been online in the last 24 hours.'),
      },
      [DeleteActionState.INTEGRATION_NON_INTEGRATION_ENTITIES]: {
        title: translateFn('Cannot delete entities'),
        message: translateFn(
          'You cannot delete integration and non-integration entities, or entities from different integrations, at the same time. Change your selection and try again.'
        ),
      },
    };

    const response: DeleteEntityPopUpSpecifics = {
      isError: false,
      states: {},
    };

    const basicValidationResponse = await this.performBasicValidation(selectedTargets);
    if (!basicValidationResponse.isValid) {
      return this.setErrorMessageAndReturnResponse(basicValidationResponse, response, deleteErrorConfigs);
    }

    const specialFoldersValidationResponse = await this.processSpecialFolders(selectedTargets, response.states);
    if (!specialFoldersValidationResponse.isValid) {
      return this.setErrorMessageAndReturnResponse(specialFoldersValidationResponse, response, deleteErrorConfigs);
    }

    const recordsToProcess = this.filterRecordsToProcess(selectedTargets);
    if (!recordsToProcess.length) return response;

    // check if we have Active Directory Entities present
    const activeDirectoryValidationResponse = this.processActiveDirectoryEntities(recordsToProcess, response.states);

    if (!activeDirectoryValidationResponse.isValid) {
      return this.setErrorMessageAndReturnResponse(activeDirectoryValidationResponse, response, deleteErrorConfigs);
    }

    if (response.states[DeleteActionState.AD_ENTITIES_IN_SELECTED_ITEMS]) {
      return response;
    }

    // check if we have AWS entities present
    const awsValidationResponse = this.processAwsEntities(recordsToProcess, response.states);

    if (!awsValidationResponse.isValid) {
      return this.setErrorMessageAndReturnResponse(awsValidationResponse, response, deleteErrorConfigs);
    }

    if (response.states[DeleteActionState.AWS_ENTITIES_IN_SELECTED_ITEMS]) {
      return response;
    }

    const parentIds = this.getUniqueParentIds(recordsToProcess);
    const parentEntities = await this.getProtectedEntitiesById(parentIds);
    const excludedParentIds = this.getExcludedParentIds(parentEntities);

    if (!excludedParentIds.length) {
      const entitiesValidationResponse = await this.processEntitiesForDelete(selectedTargets, response.states);

      if (!entitiesValidationResponse.isValid) {
        return this.setErrorMessageAndReturnResponse(entitiesValidationResponse, response, deleteErrorConfigs);
      } else {
        return response;
      }
    }

    response.states[DeleteActionState.ENDPOINTS_IN_EXCLUDED_FOLDER] = true;
    const entitiesNotInExcluded = this.filterEntitiesNotInExcluded(selectedTargets, excludedParentIds);

    const remainingEntitiesValidationResponse = await this.processEntitiesForDelete(
      entitiesNotInExcluded,
      response.states
    );

    if (!remainingEntitiesValidationResponse.isValid) {
      return this.setErrorMessageAndReturnResponse(remainingEntitiesValidationResponse, response, deleteErrorConfigs);
    }

    return response;
  }

  private static setErrorMessageAndReturnResponse(
    validationResponse: ValidationResponse,
    response: DeleteEntityPopUpSpecifics,
    deleteErrorConfigs: DeleteErrorConfigs
  ) {
    if (validationResponse.isValid) {
      return response;
    }

    if (!validationResponse.errorCode) {
      throw new Error('Programmatic error. Error set without error code.');
    }

    const errorConfig = deleteErrorConfigs[validationResponse.errorCode];

    response.isError = true;
    response.errorData = {
      errorMessages: [errorConfig.message],
      title: errorConfig.title,
      buttonText: translateFn('Cancel'),
    };

    return response;
  }

  private static async performBasicValidation(records: Array<ProtectedEntity>): Promise<ValidationResponse> {
    const validator: BaseNetworkActionValidator = NetworkActionValidatorsFactory.create(
      GlobalNetworkAction.DELETE_ENTITY
    );

    for (const record of records) {
      try {
        validator.canPerformAction(record);
      } catch (error) {
        return {
          isValid: false,
          errorCode: error.code,
        };
      }
    }

    return {
      isValid: true,
    };
  }

  private static async processSpecialFolders(
    records: Array<ProtectedEntity>,
    validCases: ValidDeleteStates
  ): Promise<ValidationResponse> {
    const companyFolderIds = this.getEntitiesByTypes(records, [Constants.Companies.TYPE_COMPANY_FOLDER]).map(
      (record: ProtectedEntity) => record._id
    );

    if (companyFolderIds.length) {
      const companyFoldersValidationResponse = await this.processCompanyFoldersForDelete(companyFolderIds, validCases);
      if (!companyFoldersValidationResponse.isValid) {
        return companyFoldersValidationResponse;
      }
    }

    const containerFolderIds = this.getEntitiesByTypes(records, [
      Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER,
    ]).map((record: ProtectedEntity) => record._id);

    if (containerFolderIds.length) {
      const containerFoldersValidationResponse = await this.processContainerFoldersForDelete(
        containerFolderIds,
        validCases
      );
      if (!containerFoldersValidationResponse.isValid) {
        return containerFoldersValidationResponse;
      }
    }

    return {
      isValid: true,
    };
  }

  private static async processEntitiesForDelete(
    records: Array<ProtectedEntity>,
    validCases: ValidDeleteStates
  ): Promise<ValidationResponse> {
    let validation = { isValid: true };
    const customGroups: Array<ProtectedEntity> = [];

    for (const record of records) {
      if (!validation.isValid) {
        return validation;
      }

      switch (record.type) {
        case Constants.ProtectedEntities.EPS.TYPE_COMPUTER:
          validation = DeleteActionHelper.processComputer(record, validCases);
          break;
        case Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP:
          customGroups.push(record);
          break;
        default:
          console.error(`Unknown entity type: ${record.type}`);
          break;
      }
    }

    if (!validation.isValid) {
      return validation;
    }

    if (!customGroups.length) {
      return validation;
    }

    await DeleteActionHelper.processComputerFoldersForDelete(
      customGroups.map(record => record._id),
      validCases
    );

    return validation;
  }

  private static processComputer(record: ProtectedEntity, validCases: ValidDeleteStates): ValidationResponse {
    if (!record.specifics.isManaged) {
      validCases[DeleteActionState.UNMANAGED_ENDPOINTS_IN_SELECTED_ITEMS] = true;
      return { isValid: true };
    }

    if (record.specifics.isContainerHost) {
      return DeleteActionHelper.processContainerHost(record, validCases);
    }

    if (record.specifics.isSva) {
      validCases[DeleteActionState.SVA_IN_SELECTED_ITEMS] = true;
      return { isValid: true };
    }

    validCases[DeleteActionState.MANAGED_ENDPOINTS_IN_SELECTED_ITEMS] = true;
    return { isValid: true };
  }

  private static processContainerHost(record: ProtectedEntity, validCases: ValidDeleteStates): ValidationResponse {
    if (record.isProtectedBySecurityContainer) {
      return {
        isValid: false,
        errorCode: DeleteActionState.CONTAINER_HOST_VM_PROTECTED_BY_SECURITY_CONTAINER_IN_SELECTED_ITEMS,
      };
    }

    if (record.specifics.isOnline) {
      return {
        isValid: false,
        errorCode: DeleteActionState.CONTAINER_HOST_VM_ONLINE_IN_SELECTED_ITEMS,
      };
    }

    const userData = Application.Main.getUser();
    // @ts-ignore
    const lastSyncDate = new Date(record.specifics.lastSync + userData.profile.timezoneOffset);
    const now = new Date();

    const elapsed = now.getTime() - lastSyncDate.getTime();

    if (elapsed < TimeUnit.DAY) {
      return {
        isValid: false,
        errorCode: DeleteActionState.CONTAINER_HOST_VM_ONLINE_IN_SELECTED_ITEMS,
      };
    }

    if (!validCases[DeleteActionState.CONTAINER_HOST_VM_DELETABLE_IN_SELECTED_ITEMS]) {
      validCases[DeleteActionState.CONTAINER_HOST_VM_DELETABLE_IN_SELECTED_ITEMS] = true;
    }

    return {
      isValid: true,
    };
  }

  private static async processCompanyFoldersForDelete(
    recordIds: Array<string>,
    validCases: ValidDeleteStates
  ): Promise<ValidationResponse> {
    const companiesInFolder: number = await ConsoleRequest.createRequest<number>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'Company',
      method: 'getNumberOfCompaniesInContainer',
      payload: {
        recordId: recordIds,
      },
    });

    if (companiesInFolder) {
      return {
        isValid: false,
        errorCode: DeleteActionState.NOT_EMPTY_COMPANY_FOLDER_IN_SELECTED_ITEMS,
      };
    }

    validCases[DeleteActionState.EMPTY_COMPANY_FOLDER_IN_SELECTED_ITEMS] = true;
    return {
      isValid: true,
    };
  }

  private static async processContainerFoldersForDelete(
    recordIds: Array<string>,
    validCases: ValidDeleteStates
  ): Promise<ValidationResponse> {
    const endpointsInFolders: number = await ConsoleRequest.createRequest<number>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'checkIfGroupContainsHosts',
      payload: {
        recordId: recordIds,
      },
    });

    if (endpointsInFolders) {
      return {
        isValid: false,
        errorCode: DeleteActionState.CONTAINER_GROUP_HAS_CONTAINER_HOSTS_IN_SELECT_ITEMS,
      };
    }

    validCases[DeleteActionState.EMPTY_CONTAINER_GROUP_IN_SELECTED_ITEMS] = true;
    return {
      isValid: true,
    };
  }

  private static async processComputerFoldersForDelete(
    recordIds: Array<string>,
    validCases: ValidDeleteStates
  ): Promise<void> {
    const endpointsInFolders: { [groupId: string]: number } = await ConsoleRequest.createRequest<{
      [groupId: string]: number;
    }>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getNumberOfComputersInGroup',
      payload: {
        recordId: recordIds,
        returnGrouped: true,
      },
    });

    for (const groupId in endpointsInFolders) {
      if (!endpointsInFolders.hasOwnProperty(groupId)) {
        continue;
      }

      if (endpointsInFolders[groupId]) {
        validCases[DeleteActionState.NOT_EMPTY_CUSTOM_GROUPS_FOLDER_IN_SELECTED_ITEMS] = true;
        continue;
      }

      validCases[DeleteActionState.EMPTY_CUSTOM_GROUPS_FOLDER_IN_SELECTED_ITEMS] = true;
    }
  }

  private static getEntitiesByTypes(
    records: Array<ProtectedEntity>,
    types: Array<number>,
    filterOut: boolean = false
  ): Array<ProtectedEntity> {
    if (!filterOut) {
      return records.filter(record => types.includes(record.type));
    }

    return records.filter(record => !types.includes(record.type));
  }

  private static filterRecordsToProcess(records: Array<ProtectedEntity>): Array<ProtectedEntity> {
    return DeleteActionHelper.getEntitiesByTypes(
      records,
      [Constants.Companies.TYPE_COMPANY_FOLDER, Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER],
      true
    );
  }

  private static getUniqueParentIds(records: Array<ProtectedEntity>): Array<string> {
    return Array.from(new Set(records.map(record => record.parentId)));
  }

  private static async getProtectedEntitiesById(parentIds: Array<string>): Promise<Array<ProtectedEntity>> {
    const consoleResponse: { data: Array<ProtectedEntity>; total: number; success: boolean } =
      await ConsoleRequest.createRequest({
        dataProviderType: DataProviderType.CORE,
        dataProviderEntity: DataProviderEntity.MODEL,
        endpoint: 'ProtectedEntitiesEPS',
        method: 'getProtectedEntityById',
        payload: {
          protectedEntityId: parentIds,
          selectedFields: ['type'],
          page: 1,
          limit: 0,
          sort: null,
          dir: null,
          filter: [],
        },
      });

    if (!consoleResponse.success) {
      throw new Error(`Failed to get entity details for ${parentIds.join(', ')}`);
    }

    return consoleResponse.data;
  }

  private static getExcludedParentIds(parentEntities: Array<ProtectedEntity>): Array<string> {
    return parentEntities
      .filter(record => record.type === Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER)
      .map(record => record._id);
  }

  private static filterEntitiesNotInExcluded(
    records: Array<ProtectedEntity>,
    excludedParentIds: Array<string>
  ): Array<ProtectedEntity> {
    return records.filter(record => !excludedParentIds.includes(record.parentId));
  }

  public static async displayDeletePopUp(selectedItems: Array<ProtectedEntity>, parentElementRef: HTMLElement) {
    const config = NetworkActionsToPopupsMap.get(GlobalNetworkAction.DELETE_ENTITY);
    if (!config) {
      console.error('No popup type found for action ', GlobalNetworkAction.DELETE_ENTITY);
      return;
    }

    analytics.trackPageInteraction({
      reference: `${NetworkV2Analytics.TRACKING_NAME}:${NetworkV2Analytics.IDENTIFIER_ACTION_CLICKED}`,
      asset: GlobalNetworkAction.DELETE_ENTITY,
    });

    try {
      const popUpRenderData: DeleteEntityPopUpSpecifics = await DeleteActionHelper.processAction(selectedItems);

      if (popUpRenderData.isError) {
        NetworkPopUpFactory.getContent(NetworkPopUpType.ACTION_ERROR_POP_UP, {
          specifics: popUpRenderData.errorData,
          targets: [],
          parentElementRef,
        });

        return;
      }

      NetworkPopUpFactory.getContent<DeleteEntityPopUpSpecifics>(config.popupType, {
        ...config.extraPopupData,
        parentElementRef,
        targets: selectedItems,
        specifics: popUpRenderData,
      });
    } catch (exception) {
      console.error('Could not process delete entities.', exception);
    }
  }

  private static processActiveDirectoryEntities(
    recordsToProcess: Array<ProtectedEntity>,
    validCases: ValidDeleteStates
  ): ValidationResponse {
    const activeDirectoryEntities: Array<ProtectedEntity> = recordsToProcess.filter(record =>
      [
        Constants.ProtectedEntities.EPS.ACTIVE_DIRECTORY,
        Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER_GROUP,
        Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER,
      ].includes(record.type)
    );

    if (!activeDirectoryEntities.length) {
      return { isValid: true };
    }

    const hasOnlyActiveDirectoryEntities: boolean = activeDirectoryEntities.length === recordsToProcess.length;

    // error case with combo Active Directory and normal entities
    if (!hasOnlyActiveDirectoryEntities) {
      return {
        isValid: false,
        errorCode: DeleteActionState.INTEGRATION_NON_INTEGRATION_ENTITIES,
      };
    }

    validCases[DeleteActionState.AD_ENTITIES_IN_SELECTED_ITEMS] = true;
    return {
      isValid: true,
    };
  }

  private static processAwsEntities(
    recordsToProcess: Array<ProtectedEntity>,
    states: ValidDeleteStates
  ): ValidationResponse {
    const awsEntities: Array<ProtectedEntity> = recordsToProcess.filter(
      record => record.type === Constants.ProtectedEntities.EPS.TYPE_AWS_INSTANCE
    );

    if (!awsEntities.length) {
      return { isValid: true };
    }

    const hasOnlyAwsEntities: boolean = awsEntities.length === recordsToProcess.length;

    if (!hasOnlyAwsEntities) {
      return {
        isValid: false,
        errorCode: DeleteActionState.INTEGRATION_NON_INTEGRATION_ENTITIES,
      };
    }

    states[DeleteActionState.AWS_ENTITIES_IN_SELECTED_ITEMS] = true;
    return {
      isValid: true,
    };
  }
}
